import { fetchLimitSummaryRequest } from 'tio-data/requests/limit';
import type { SummaryResponse } from 'tio-data/requests/limit';

/**
 * App-level stale-while-revalidate cache for /api/v2/limits/summary.
 *
 * The payload is not JSON:API-shaped, so the builder (correctly) opts out of
 * the store's document cache with SkipCache — which made every mount of the
 * benefit cards and the limit tree refetch and redraw. This module gives those
 * components the missing behavior: an instant paint from the last response,
 * one shared in-flight request per key (the cards and the tree on the same
 * page produce a single fetch), and a quiet background refresh once the entry
 * is older than {@link CACHE_FRESH_MS}.
 */

interface StoreLike {
  request(req: ReturnType<typeof fetchLimitSummaryRequest>): Promise<{ content: unknown }>;
}

export interface LimitSummaryParams {
  employeeId: string;
  date?: string;
}

const CACHE_FRESH_MS = 10_000;

const summaryCache = new Map<string, { data: SummaryResponse; fetchedAt: number }>();
const inFlight = new Map<string, Promise<SummaryResponse>>();

function summaryCacheKey(params: LimitSummaryParams) {
  return `${params.employeeId}|${params.date ?? 'current'}`;
}

function requestSummary(
  store: StoreLike,
  params: LimitSummaryParams,
  key: string
): Promise<SummaryResponse> {
  const existing = inFlight.get(key);
  if (existing) return existing;

  const promise = (async () => {
    try {
      const response = await store.request(fetchLimitSummaryRequest(params));
      const data = (response.content as { data: SummaryResponse }).data;
      summaryCache.set(key, { data, fetchedAt: Date.now() });
      return data;
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, promise);
  return promise;
}

/**
 * Resolve the summary for the given params: cached data returns immediately
 * (revalidating in the background once stale — `onRevalidated` fires after the
 * cache has been updated so callers can re-read); a cache miss awaits the
 * network. Background refresh failures are swallowed: the cached view stands
 * until a foreground fetch replaces it.
 */
export function fetchLimitSummaryCached(
  store: StoreLike,
  params: LimitSummaryParams,
  onRevalidated?: () => void
): Promise<SummaryResponse> {
  const key = summaryCacheKey(params);
  const cached = summaryCache.get(key);
  if (!cached) return requestSummary(store, params, key);

  if (Date.now() - cached.fetchedAt > CACHE_FRESH_MS) {
    // requestSummary dedupes the network call; chaining here (rather than
    // guarding on inFlight) means EVERY stale consumer's callback fires when
    // the one shared refresh lands — the cards and the tree both update.
    void requestSummary(store, params, key)
      .then(() => onRevalidated?.())
      .catch(() => {});
  }
  return Promise.resolve(cached.data);
}

/** Test hook: reset all cached entries and in-flight requests. */
export function clearLimitSummaryCache() {
  summaryCache.clear();
  inFlight.clear();
}
